import { useState } from 'react';
import { Popover } from 'antd';
import clsx from 'clsx';
import type { GroupIconReference } from '../../../../../services/GroupManager/groupManager.service';
import { fallbackIconAt } from './constants';
import styles from './groupIconPicker.module.scss';

export interface GroupIconPickerProps {
  icons: GroupIconReference[];
  selectedIconId: number | null;
  onSelect: (id: number) => void;
}

interface GroupIconImageProps {
  icon: GroupIconReference;
  index: number;
  className?: string;
}

// Falls back to a local glyph when the icon's image can't be loaded.
const GroupIconImage = ({ icon, index, className }: GroupIconImageProps) => {
  const [hasFailed, setHasFailed] = useState(false);
  const FallbackIcon = fallbackIconAt(index);

  if (hasFailed) return <FallbackIcon className={className} />;
  return <img src={icon.url} alt="" className={className} onError={() => setHasFailed(true)} />;
};

interface IconOptionButtonProps {
  icon: GroupIconReference;
  index: number;
  isSelected: boolean;
  onSelect: (id: number) => void;
}

const IconOptionButton = ({ icon, index, isSelected, onSelect }: IconOptionButtonProps) => {
  const handleClick = () => onSelect(icon.id);

  return (
    <button
      type="button"
      className={clsx(styles.iconOption, isSelected && styles.iconOptionSelected)}
      aria-pressed={isSelected}
      onClick={handleClick}
    >
      <GroupIconImage icon={icon} index={index} className={styles.optionImage} />
    </button>
  );
};

const GroupIconPicker = ({ icons, selectedIconId, onSelect }: GroupIconPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedIndex = icons.findIndex((icon) => icon.id === selectedIconId);

  const handleSelect = (id: number) => {
    onSelect(id);
    setIsOpen(false);
  };

  const grid = (
    <div className={styles.grid}>
      {icons.length === 0 ? (
        <p className={styles.empty}>No icons available</p>
      ) : (
        icons.map((icon, index) => (
          <IconOptionButton key={icon.id} icon={icon} index={index} isSelected={selectedIconId === icon.id} onSelect={handleSelect} />
        ))
      )}
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} trigger="click" placement="bottomRight" overlayClassName={styles.popoverOverlay} content={grid}>
      <button type="button" className={styles.trigger} aria-label="Choose group icon">
        {selectedIndex >= 0 ? (
          <GroupIconImage key={icons[selectedIndex].id} icon={icons[selectedIndex]} index={selectedIndex} className={styles.triggerIcon} />
        ) : (
          <img src="/assets/selectImageIcon.svg" alt="" className={styles.triggerIcon} />
        )}
      </button>
    </Popover>
  );
};

export default GroupIconPicker;
